import { type Signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { SignalStore } from '@move-frontend/portal-toolkit';
import { catchError, debounce, map, of, Subject, switchMap, timer, type Observable } from 'rxjs';
import { DEFAULT_LIMIT, type Paginated } from '../pagination';

const SEARCH_DEBOUNCE_MS: Readonly<number> = 250;

type SelectStatus = 'loading' | 'loaded' | 'error';
type LoadMode = 'replace' | 'append';

export type PaginatedSelectRequest = Readonly<{
  searchTerm: string;
  offset: number;
  limit: number;
  params: Readonly<Record<string, string>>;
}>;

export type PaginatedSelectFetch<T> = (request: PaginatedSelectRequest) => Observable<Paginated<T>>;

type PaginatedSelectState<T> = {
  items: ReadonlyArray<T>;
  searchTerm: string;
  params: Readonly<Record<string, string>>;
  offset: number;
  hasMore: boolean;
  status: SelectStatus;
  selected: T | null;
};

type Trigger = Readonly<{ mode: LoadMode; debounced: boolean }>;
type FetchOutcome<T> = Readonly<{ mode: LoadMode; page: Paginated<T>; failed: boolean }>;

/**
 * Backend-paginated state for a single-select filter: type-ahead search (debounced,
 * replaces the list) and infinite scroll (loadMore, appends the next page). Concrete
 * stores supply the page-fetching function; the rest is shared.
 */
export abstract class PaginatedSelectStore<T extends { id: string }> extends SignalStore<PaginatedSelectState<T>> {
  readonly items: Signal<ReadonlyArray<T>>;
  readonly options: Signal<ReadonlyArray<T>>;
  readonly selected: Signal<T | null>;
  readonly searchTerm: Signal<string>;
  readonly isLoading: Signal<boolean>;
  readonly hasError: Signal<boolean>;
  readonly hasMore: Signal<boolean>;

  private readonly trigger: Subject<Trigger> = new Subject<Trigger>();

  protected constructor(fetchPage: PaginatedSelectFetch<T>) {
    super();
    this.initialize({
      items: [],
      searchTerm: '',
      params: {},
      offset: 0,
      hasMore: false,
      status: 'loading',
      selected: null,
    });

    this.items = this.select('items');
    this.selected = this.select('selected');
    this.searchTerm = this.select('searchTerm');
    this.isLoading = this.select('status', (status: SelectStatus): boolean => status === 'loading');
    this.hasError = this.select('status', (status: SelectStatus): boolean => status === 'error');
    this.hasMore = this.select('hasMore');
    this.options = this.selectMany(
      ['items', 'selected'],
      (state: PaginatedSelectState<T>): ReadonlyArray<T> => mergeSelected(state.selected, state.items),
    );

    this.trigger
      .pipe(
        debounce((next: Trigger): Observable<number> => timer(next.debounced ? SEARCH_DEBOUNCE_MS : 0)),
        switchMap((next: Trigger): Observable<FetchOutcome<T>> => {
          const request: PaginatedSelectRequest = {
            searchTerm: this.snapshot.searchTerm,
            offset: next.mode === 'append' ? this.snapshot.items.length : 0,
            limit: DEFAULT_LIMIT,
            params: this.snapshot.params,
          };
          return fetchPage(request).pipe(
            map((page: Paginated<T>): FetchOutcome<T> => ({ mode: next.mode, page, failed: false })),
            catchError(
              (): Observable<FetchOutcome<T>> => of({ mode: next.mode, page: { items: [], total: 0 }, failed: true }),
            ),
          );
        }),
        takeUntilDestroyed(),
      )
      .subscribe((outcome: FetchOutcome<T>): void => this.applyOutcome(outcome));

    this.trigger.next({ mode: 'replace', debounced: false });
  }

  search(term: string): void {
    this.patch({ searchTerm: term, status: 'loading' });
    this.trigger.next({ mode: 'replace', debounced: true });
  }

  loadMore(): void {
    if (this.isLoading() || !this.hasMore()) return;
    this.patch({ status: 'loading' });
    this.trigger.next({ mode: 'append', debounced: false });
  }

  setSelected(item: T | null): void {
    this.patch({ selected: item });
  }

  reset(): void {
    this.patch({ searchTerm: '', selected: null, status: 'loading' });
    this.trigger.next({ mode: 'replace', debounced: false });
  }

  protected setParams(params: Readonly<Record<string, string>>): void {
    if (sameParams(this.snapshot.params, params)) return;
    this.patch({ params, status: 'loading' });
    this.trigger.next({ mode: 'replace', debounced: false });
  }

  private applyOutcome(outcome: FetchOutcome<T>): void {
    if (outcome.failed) {
      this.patch({ status: 'error' });
      return;
    }
    const items: ReadonlyArray<T> =
      outcome.mode === 'append' ? [...this.snapshot.items, ...outcome.page.items] : outcome.page.items;
    const hasMore: boolean = hasMorePages(items.length, outcome.page.items.length, DEFAULT_LIMIT, outcome.page.total);
    this.patch({ items, hasMore, offset: items.length, status: 'loaded' });
  }
}

function hasMorePages(loadedCount: number, lastPageSize: number, limit: number, total: number | undefined): boolean {
  return total === undefined ? lastPageSize === limit : loadedCount < total;
}

function mergeSelected<T extends { id: string }>(selected: T | null, items: ReadonlyArray<T>): ReadonlyArray<T> {
  if (selected === null) return items;
  return items.some((item: T): boolean => item.id === selected.id) ? items : [selected, ...items];
}

function sameParams(first: Readonly<Record<string, string>>, second: Readonly<Record<string, string>>): boolean {
  const firstKeys: ReadonlyArray<string> = Object.keys(first);
  const secondKeys: ReadonlyArray<string> = Object.keys(second);
  return (
    firstKeys.length === secondKeys.length && firstKeys.every((key: string): boolean => first[key] === second[key])
  );
}
